// src/pages/tickets/components/TicketAdjuntos.tsx
import { useRef } from 'react';
import { Card, Button, Spinner } from '@/components/ui';
import { Upload, File, Download, Trash2, FileText, Image, FileSpreadsheet } from 'lucide-react';
import { formatDateTime, cn } from '@/lib/utils';
import { useAdjuntos, useSubirAdjunto, useEliminarAdjunto } from '@/hooks/queries/useTickets';
import { ticketsService } from '@/services/tickets';
import { useAuthStore } from '@/stores/authStore';

interface Props {
  ticketId: string;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function getIcono(mimeType?: string) {
  if (!mimeType) return <File className="h-8 w-8 text-gray-400" />;
  if (mimeType.startsWith('image/')) return <Image className="h-8 w-8 text-green-500" />;
  if (mimeType === 'application/pdf') return <FileText className="h-8 w-8 text-red-500" />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel'))
    return <FileSpreadsheet className="h-8 w-8 text-green-600" />;
  return <File className="h-8 w-8 text-gray-400" />;
}

export function TicketAdjuntos({ ticketId }: Props) {
  const { data: adjuntos, isLoading } = useAdjuntos(ticketId);
  const subirMutation = useSubirAdjunto();
  const eliminarMutation = useEliminarAdjunto();
  const { user } = useAuthStore();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      await subirMutation.mutateAsync({ ticketId, file });
    }

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleDescargar = async (adjuntoId: string, nombreOriginal: string) => {
    try {
      const blob = await ticketsService.downloadAdjunto(ticketId, adjuntoId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombreOriginal);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar:', error);
    }
  };

  const handleEliminar = async (adjuntoId: string) => {
    if (confirm('Eliminar este archivo?')) {
      await eliminarMutation.mutateAsync({ ticketId, adjuntoId });
    }
  };

  if (isLoading) {
    return (
      <Card className="p-4 flex justify-center">
        <Spinner />
      </Card>
    );
  }

  return (
    <Card className="p-4">
      {/* Upload area */}
      <div
        className={cn(
          'border-2 border-dashed rounded-lg p-8 text-center mb-4 transition-colors',
          'border-gray-300 hover:border-gray-400'
        )}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={handleFileChange}
          className="hidden"
          id="file-upload"
        />
        <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <label htmlFor="file-upload" className="cursor-pointer">
          <span className="text-sm text-gray-600">
            Haga clic para seleccionar archivos
          </span>
          <p className="text-xs text-gray-400 mt-1">Maximo 10MB por archivo</p>
        </label>
      </div>

      {subirMutation.isPending && (
        <div className="flex items-center gap-2 text-sm text-blue-600 mb-4">
          <Spinner size="sm" />
          Subiendo archivo...
        </div>
      )}

      {/* Lista de adjuntos */}
      <div className="space-y-2">
        {adjuntos?.map((adjunto) => (
          <div key={adjunto.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            {getIcono(adjunto.mime_type)}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{adjunto.nombre_original}</p>
              <p className="text-xs text-gray-500">
                {formatBytes(adjunto.tamano_bytes)} - {adjunto.subido_por?.nombre} -{' '}
                {formatDateTime(adjunto.created_at)}
              </p>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDescargar(adjunto.id, adjunto.nombre_original)}
              >
                <Download className="h-4 w-4" />
              </Button>
              {adjunto.subido_por_id === user?.id && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleEliminar(adjunto.id)}
                  className="text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}

        {adjuntos?.length === 0 && (
          <p className="text-center text-gray-500 py-4">No hay archivos adjuntos</p>
        )}
      </div>
    </Card>
  );
}
